//—— 故事頁的開場：白夜看完竹冊、然後批命 ——
//
//  這兩幕本來在劇情頁（SCENES 的 verdict 與 reading）。搬過來的理由是時間：
//  故事頁一掛載就開始生成那四塊命書（約五秒），而這兩支影片合計十七秒半
//  且中間還要按一次「開始解命」—— 生成被完整藏在劇情底下，
//  使用者看到的是白夜在算，不是一顆轉圈圈的 loading。
//
//  —— 為什麼不沿用劇情頁那套場景機制 ——
//  那套要服務十六幕：分支、問診表單、返回、標語輪播、影片循環、前瞻預載。
//  這裡只有「播兩支、中間按一次、播完就走」，把那套搬過來等於為了兩幕
//  扛一整台狀態機。兩邊會各自演化，但各自的需求本來就不一樣。
//
//  共用的是「字長什麼樣子」（styles/_mixins 的 drama-line）與影片檔本身 ——
//  那兩樣不一致才是真的會被看出來。

import { useCallback, useEffect, useRef, useState } from "react";

import "./baiye-story-style.scss";
import videoUrl from "../../../../utils/video-url";

const VIDEO_VERDICT = videoUrl("baiye-4.mp4");
const VIDEO_READING = videoUrl("baiye-5.mp4");

//跳過影片開頭那一格。理由同劇情頁的 INTRO_SKIP_S：
//  第 0 格是一張還沒動起來的靜止畫面，從那裡起播會先愣一下。
const SKIP_S = 0.3;

//—— 台詞 ——
//  跟搬過來之前一模一樣（原 SCENES.verdict.lines）。
//  「呵！」是他抬起頭的那一聲，不是句子的一部分，所以自己站一句、字級拉大。
const LINES = [
  { text: "呵！", max: 64 },
  { text: "原來是這般命格", max: 0 },
];

//比別句晚開口。2000 是對著影片逐格抓的：
//  baiye-4 在 1.3 秒還是面無表情，1.8 秒嘴角開始動，2.3 秒才是完整的冷笑。
//  影片從 0.3 秒起播，淡入吃掉 300ms，所以字全亮時影片在 2.6 秒，笑容已經成形。
const LEAD_IN_MS = 2000;

//一句台詞停多久。短句吃 1500 的下限，長句按字數加 ——
//  數字與劇情頁的 lineDuration 一致，兩邊的節奏才像同一個人在說話。
const lineDuration = (text) => Math.max(1500, 900 + String(text).length * 100);

//一句裡最長的一段有幾個字（字級算式要用，見 drama-line mixin）
const longestRun = (text) =>
  String(text || "")
    .split("\n")
    .reduce((max, part) => Math.max(max, part.length), 0);

//影片播完之後、換下一件事之前留的一口氣。
//  最後一格才剛停住就切走，看起來像被彈出去的。
const BEAT_MS = 500;

const BaiyeStoryIntro = ({ onReading, onDone }) => {
  //0 = 他低頭看竹冊（baiye-4）、1 = 他批命（baiye-5）
  const [phase, setPhase] = useState(0);
  const [lineIndex, setLineIndex] = useState(0);
  const [verdictEnded, setVerdictEnded] = useState(false);
  //影片真的動起來了才讓它顯示 —— 直接顯示的話會先閃一格黑
  const [playing, setPlaying] = useState(false);
  const [leaving, setLeaving] = useState(false);

  //瀏覽器擋掉有聲自動播放時退回靜音，並露出「開啟聲音」。
  //  多數人是從劇情頁走過來的（已經互動過），所以通常不會走到這裡。
  const [muted, setMuted] = useState(false);
  const [soundBlocked, setSoundBlocked] = useState(false);

  const videoA = useRef(null);
  const videoB = useRef(null);
  const ref = (i) => (i === 0 ? videoA : videoB);

  //兩層都掛著：baiye-5 在 baiye-4 播的時候就開始載，
  //  按下「開始解命」才不會卡一下才動。
  const SRC = [VIDEO_VERDICT, VIDEO_READING];

  //—— 播放 ——
  //  帶聲音的自動播放被擋就退回靜音續播。畫面不能因為沒聲音就停住。
  useEffect(() => {
    const video = ref(phase).current;
    if (!video) return;
    video.currentTime = SKIP_S;
    video.play().catch(() => {
      setMuted(true);
      setSoundBlocked(true);
      video.muted = true;
      video.play().catch(() => {
        //連靜音都播不動（檔案壞了、解碼失敗）：不要卡在這裡，直接讓路
        setPlaying(true);
        if (phase === 0) setVerdictEnded(true);
        else onDone?.();
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  //—— 台詞一句一句往下推 ——
  //  影片真的動起來才起算，不然字會在畫面還黑的時候就跑掉。
  useEffect(() => {
    if (phase !== 0 || !playing) return undefined;
    if (lineIndex >= LINES.length) return undefined;
    const wait =
      lineDuration(LINES[lineIndex].text) + (lineIndex === 0 ? LEAD_IN_MS : 0);
    const timer = setTimeout(() => setLineIndex((i) => i + 1), wait);
    return () => clearTimeout(timer);
  }, [phase, playing, lineIndex]);

  const handlePlaying = () => setPlaying(true);

  const handleEnded = (i) => {
    if (i !== phase) return;
    if (i === 0) setVerdictEnded(true);
    else {
      //批命播完就讓路。先淡出再通知，不然故事頁會在影片還在畫面上時就冒出來
      setLeaving(true);
      setTimeout(() => onDone?.(), BEAT_MS);
    }
  };

  const startReading = useCallback(() => {
    if (soundBlocked) {
      //按鈕本身就是一次使用者互動，順手把被擋掉的聲音要回來
      setMuted(false);
      setSoundBlocked(false);
    }
    //那四塊命書從這一刻開始生成（約 6 秒）。
    //  擺在這裡而不是元件掛載時：生成是付費前的成本，每個走到這裡的人
    //  都要燒一次。掛載就發的話，在第一支影片中途關掉頁面的人也算一次。
    //  按下這顆按鈕是明確的互動，意願訊號強得多。
    //
    //  掩護還夠：接下來 baiye-5 要播 8 秒（不能跳過），
    //  之後還要滑到整頁的 15% 才看得到第一塊面板。
    onReading?.();
    setPlaying(false);
    setPhase(1);
  }, [soundBlocked, onReading]);

  const line = phase === 0 && lineIndex < LINES.length ? LINES[lineIndex] : null;

  return (
    <div
      className={`baiye-story__intro${leaving ? " is-leaving" : ""}`}
      //純演出，不是可以操作的東西
      aria-hidden="true"
    >
      {[0, 1].map((i) => (
        <video
          key={i}
          ref={ref(i)}
          className={`baiye-story__intro-video${
            i === phase && playing ? " is-front" : ""
          }`}
          src={SRC[i]}
          //背面那層一律靜音，不然按下按鈕的瞬間會聽到兩軌疊在一起
          muted={muted || i !== phase}
          playsInline
          preload="auto"
          //不能用 loop：這兩支都要發 ended（一支等按鈕、一支等離場），
          //  而循環的影片永遠不會發。
          onLoadedMetadata={(e) => {
            e.currentTarget.currentTime = SKIP_S;
          }}
          onPlaying={handlePlaying}
          onEnded={() => handleEnded(i)}
          //載不出來就讓路，不要把人卡在一片黑裡
          onError={() => handleEnded(i)}
        />
      ))}

      {/* 底部漸層：按鈕是半透明的，背後剛好是亮畫面時沒有這層會糊掉 */}
      <div className="baiye-story__intro-veil" />

      {/* key 綁 lineIndex：換句時整個重新掛載，淡入淡出才會重播 */}
      {line && playing && (
        <div className="baiye-story__intro-center">
          <p
            className="baiye-story__intro-line"
            key={lineIndex}
            style={{
              "--line-chars": longestRun(line.text),
              ...(line.max ? { "--line-max": `${line.max}px` } : null),
              animationDuration: `${lineDuration(line.text)}ms`,
              ...(lineIndex === 0 ? { animationDelay: `${LEAD_IN_MS}ms` } : null),
            }}
          >
            {line.text}
          </p>
        </div>
      )}

      {/* 影片播完才出現。等的是影片不是台詞 ——
          他還在看竹冊的時候把按鈕推到面前，等於在催他。 */}
      {phase === 0 && verdictEnded && (
        <div className="baiye-story__intro-panel">
          <button
            type="button"
            className="baiye-story__intro-btn"
            onClick={startReading}
          >
            開始解命
          </button>
        </div>
      )}

      {soundBlocked && (
        <button
          type="button"
          className="baiye-story__intro-sound"
          onClick={() => {
            setMuted(false);
            setSoundBlocked(false);
          }}
        >
          <span aria-hidden="true">🔇</span>
          開啟聲音
        </button>
      )}
    </div>
  );
};

export default BaiyeStoryIntro;
